// services/storageService.ts - Web platform only
import { GameState } from '../constants/types';

// Note: saveImageToLocal is not available on web platform
export async function saveImageToLocal(
  imageDataOrUrl: string,
  filename = `via_logos_image_${Date.now()}.png`
): Promise<string> {
  throw new Error('saveImageToLocal is not available on web platform');
}

export interface AutosaveMetadata {
  id: string;
  name: string;
  bibleVerse: string;
  timestamp: number;
  turns: number;
  sessionMinutes: number;
}

const GAME_FILE_EXTENSION = '.via';
const AUTOSAVE_LIST_KEY = 'via_logos_autosaves_list';

// Helper to get localStorage
export const getItem = async (key: string): Promise<string | null> => {
  return Promise.resolve(localStorage.getItem(key));
};

export const setItem = async (key: string, value: string): Promise<void> => {
  localStorage.setItem(key, value);
  return Promise.resolve();
};

export const removeItem = async (key: string): Promise<void> => {
  console.log('Autosave key:', key);
  localStorage.removeItem(key);
  return Promise.resolve();
};

export const importGameState = async (): Promise<GameState | null> => {
  try {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = GAME_FILE_EXTENSION;
      input.onchange = (event: Event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) {
          resolve(null);
          return;
        }

        const reader = new FileReader();
        reader.onload = (e: ProgressEvent<FileReader>) => {
          try {
            const importedState = JSON.parse(e.target?.result as string);
            resolve(importedState as GameState);
          } catch (err) {
            console.error('Failed to parse save file', err);
            console.error('Error', 'Failed to parse save file. It might be corrupted.');
            reject(err);
          }
        };
        reader.onerror = err => {
          console.error('File reader error', err);
          console.error('Error', 'Failed to read file.');
          reject(err);
        };
        reader.readAsText(file);
      };
      input.click();
    });
  } catch (error) {
    console.error('Error importing game state:', error);
    console.error('Error', 'Could not import game state.');
    return null;
  }
};

export const exportGameState = async (
  gameState: GameState,
  format: 'json' | 'markdown'
): Promise<void> => {
  try {
    let content = '';
    let fileName = `${gameState.storyName.replace(/\s+/g, '_')}`;
    let mimeType = '';

    if (format === 'json') {
      content = JSON.stringify(gameState, null, 2);
      fileName += GAME_FILE_EXTENSION;
      mimeType = 'application/json';
    } else {
      // Build improved markdown content for Bible verse walkthrough
      // Title and metadata
      content += `# ${gameState.storyName}\n\n`;
      content += `**Verse:** ${gameState.bibleVerse}\n`;

      content += `\n---\n\n`;

      // Build story content with proper formatting
      gameState.history.forEach((m, index) => {
        const roleName = m.role === 'user' ? 'You' : 'Narrator';

        // Add turn number for user actions
        if (m.role === 'user') {
          content += `## Turn ${Math.ceil(index / 2) + 1}\n\n`;
        }

        // Add main content with proper paragraph spacing
        // Split by double newlines to preserve paragraph structure
        const paragraphs = m.content.split('\n\n');
        content += `**${roleName}:**\n\n`;

        paragraphs.forEach(para => {
          const trimmed = para.trim();
          if (trimmed) {
            content += `${trimmed}\n\n`;
          }
        });

        // Add image reference if present
        if (m.imageUrl) {
          content += `*[Scene Image](${m.imageUrl})*\n\n`;
        }

        // Add spacing between messages
        content += `---\n\n`;
      });

      fileName += '.md';
      mimeType = 'text/markdown';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting game state:', error);
    console.error('Error', 'Could not export game state.');
  }
};

export const saveAutosave = async (
  gameState: GameState,
  sessionMinutes: number = 0
): Promise<void> => {
  try {
    const autosaveId = gameState.id; // Use game state ID as autosave ID
    const autosaveKey = `via_logos_autosave_${autosaveId}`;
    await setItem(autosaveKey, JSON.stringify(gameState));

    // Update master list of autosaves
    const listJson = await getItem(AUTOSAVE_LIST_KEY);
    let autosaves: AutosaveMetadata[] = listJson ? JSON.parse(listJson) : [];

    const newMetadata: AutosaveMetadata = {
      id: gameState.id,
      name: gameState.storyName,
      bibleVerse: gameState.bibleVerse,
      timestamp: Date.now(),
      turns: gameState.history.length,
      sessionMinutes: sessionMinutes,
    };

    const existingIndex = autosaves.findIndex(as => as.id === autosaveId);
    if (existingIndex > -1) {
      // Preserve sessionMinutes from existing entry if not provided
      const existingSessionMinutes = autosaves[existingIndex].sessionMinutes || 0;
      newMetadata.sessionMinutes = sessionMinutes > 0 ? sessionMinutes : existingSessionMinutes;
      autosaves[existingIndex] = newMetadata; // Update existing entry
    } else {
      autosaves.push(newMetadata); // Add new entry
    }
    await setItem(AUTOSAVE_LIST_KEY, JSON.stringify(autosaves));
  } catch (error) {
    console.error('Failed to save autosave:', error);
  }
};

export const loadAutosave = async (id: string): Promise<GameState | null> => {
  try {
    const autosaveKey = `via_logos_autosave_${id}`;
    const gameStateJson = await getItem(autosaveKey);
    return gameStateJson ? JSON.parse(gameStateJson) : null;
  } catch (error) {
    console.error('Failed to load autosave:', error);
    return null;
  }
};

export const listAutosaves = async (): Promise<AutosaveMetadata[]> => {
  try {
    const listJson = await getItem(AUTOSAVE_LIST_KEY);
    return listJson ? JSON.parse(listJson) : [];
  } catch (error) {
    console.error('Failed to list autosaves:', error);
    return [];
  }
};

export const deleteAutosave = async (id: string): Promise<void> => {
  try {
    // Remove the game state itself
    const autosaveKey = `via_logos_autosave_${id}`;
    await removeItem(autosaveKey);

    // Update master list
    const listJson = await getItem(AUTOSAVE_LIST_KEY);
    console.log('Autosave list JSON:', listJson);
    let autosaves: AutosaveMetadata[] = listJson ? JSON.parse(listJson) : [];
    autosaves = autosaves.filter(as => as.id !== id);
    await setItem(AUTOSAVE_LIST_KEY, JSON.stringify(autosaves));
  } catch (error) {
    console.error('Failed to delete autosave:', error);
    throw error; // Re-throw to allow caller to handle
  }
};
